import type {
  MeetingUrl,
  MicrosoftTeamsLiveUrl,
  MicrosoftTeamsUrl,
} from "@frontierx/types";
import type { CallWithExtras } from "../types/call";

// --- Helper functions for parsing meeting URLs ---
export function getMicrosoftTeamsDisplayId(
  meetingUrl: MicrosoftTeamsUrl | MicrosoftTeamsLiveUrl,
): string {
  const { meeting_id, thread_id, message_id, business_meeting_id, tenant_id } =
    meetingUrl;
  if (meeting_id) {
    return meeting_id;
  }
  if (business_meeting_id) {
    return business_meeting_id;
  }
  if (thread_id) {
    const parts = thread_id.split(":meeting_");
    if (parts.length > 1 && parts[1]) {
      return parts[1];
    }
    return thread_id;
  }
  if (message_id) {
    return message_id;
  }
  return tenant_id ?? "Unknown";
}

export function meetingUrlToDisplayId(meetingUrl: MeetingUrl): string {
  const { platform, meeting_id } = meetingUrl;
  if (platform === "zoom" && meeting_id) {
    return meeting_id;
  }

  if (platform === "google_meet" && meeting_id) {
    return meeting_id;
  }

  if (platform === "microsoft_teams" || platform === "microsoft_teams_live") {
    return getMicrosoftTeamsDisplayId(meetingUrl);
  }
  return "Unknown";
}

export function parseStringUrlToDisplayId(url: string): string {
  try {
    const parsedUrl = new URL(url.trim());
    const hostname = parsedUrl?.hostname;
    const pathname = parsedUrl?.pathname;

    if (hostname?.includes("google")) {
      return pathname?.split("/").pop() || "Google Meet";
    }
    if (hostname?.includes("zoom")) {
      return pathname?.split("/").pop() || "Zoom Meeting";
    }
    if (hostname?.includes("teams.live.com")) {
      return pathname?.split("/").pop() || "Teams Meeting";
    }
    if (hostname?.includes("teams.microsoft")) {
      const parts = pathname?.split("/").filter(Boolean);
      const roomIdContainer = parts
        ? parts?.length > 1
          ? parts[parts.length - 2]
          : undefined
        : undefined;
      const potentialId = roomIdContainer?.split(":meeting_")[1];
      return potentialId || "Teams Meeting";
    }
    return pathname || "Meeting";
  } catch (e) {
    console.error("Error parsing string URL for display ID:", e);
    return "Meeting";
  }
}

export function getDisplayIdFromCall(call: CallWithExtras): string {
  // Handle both new 'meetingUrl' and legacy 'url' properties
  const urlValue = call.meetingUrl ?? call.url;

  if (typeof urlValue === "string") {
    return parseStringUrlToDisplayId(urlValue);
  }
  if (urlValue && typeof urlValue === "object") {
    return meetingUrlToDisplayId(urlValue as MeetingUrl);
  }
  return call.id.substring(0, 12); // Default to db id substring
}

// Helper function to format call time
export function formatCallTime(startTime: string): string {
  const start = new Date(startTime);
  const today = new Date();
  const isToday = start.toDateString() === today.toDateString();

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
  };
  const dateOptions: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
  };

  const startTimeStr = start.toLocaleTimeString(undefined, timeOptions);

  if (isToday) {
    return `Today at ${startTimeStr}`;
  }

  return `${start.toLocaleDateString(
    undefined,
    dateOptions,
  )} at ${startTimeStr}`;
}
